/**
 * Naming the vehicle on an ADAS report — "2022 Mercedes-Benz GLC 300".
 *
 * The API composes this and sends it as `vehicle.displayName` (and the model
 * on its own as `vehicle.displayModel`) so the client portal, the admin
 * portal, the report PDF, iOS and Android cannot disagree about the same
 * report. Joining year/make/model/trim by hand printed "2022 Mercedes-Benz GLC
 * GLC 300", because DataOne's trim for a GLC already contains the model.
 *
 * The local composition below is only a fallback for an API that predates the
 * field; it applies the same rules — drop the make from the model when the
 * model starts with it, drop the make and/or model from the trim when the trim
 * starts with or equals it, and a prefix only counts on a word boundary so a
 * "GL" make cannot eat the front of a "GLC 300" trim.
 */

const clean = (value) => {
  if (value === null || value === undefined) return ''
  return String(value).trim().replace(/\s+/g, ' ')
}

const startsWithWord = (value, prefix) => {
  if (value.toLowerCase().indexOf(prefix.toLowerCase()) !== 0) return false
  const next = value.charAt(prefix.length)
  return next === '' || /[^A-Za-z0-9]/.test(next)
}

const stripRedundantPrefix = (value, prefixes) => {
  const text = clean(value)
  if (!text) return ''
  const match = prefixes
    .map(clean)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .find((candidate) => startsWithWord(text, candidate))
  return match ? clean(text.slice(match.length)) : text
}

/** The model with a redundant leading make removed — headlines show no trim. */
export const vehicleDisplayModel = (vehicle) => {
  const v = vehicle || {}
  if (v.displayModel) return clean(v.displayModel)
  return stripRedundantPrefix(v.model, [v.make])
}

/** The whole name, for the "Per the … repair manual" sentence. */
export const vehicleDisplayName = (vehicle) => {
  const v = vehicle || {}
  if (v.displayName) return clean(v.displayName)

  const make = clean(v.make)
  const model = vehicleDisplayModel(v)
  const trim = stripRedundantPrefix(v.trim, [
    make && model ? `${make} ${model}` : '',
    model,
    make
  ])
  return [clean(v.year), make, model, trim].filter(Boolean).join(' ')
}
